import type {
  SinglePageReviewSerializer,
  OkResponse_SinglePageReviewSerializer_,
  IApiResponse,
  ArtReviewSerializer,
  UsersReviewsSerializer,
  IResponse,
} from "@litres-libs/types";
import { rtkqApi } from "../api/rtkqApi";

import { TagsType } from "../constants";

export const reviewsApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getReview: build.query<SinglePageReviewSerializer, { reviewId: number }>({
      query: ({ reviewId }) => {
        return {
          url: `/foundation/api/reviews/${reviewId}`,
        };
      },
      transformResponse: (response: OkResponse_SinglePageReviewSerializer_) => {
        return response.payload.data;
      },
      providesTags: (_result, _error, params) => [
        { type: TagsType.REVIEW, id: params.reviewId },
      ],
    }),

    getArtReviews: build.query<
      IApiResponse<ArtReviewSerializer[]>,
      {
        url?: string;
        artId: number;
        limit?: number;
        sortType?: "new" | "popular";
        SID?: string;
        withReplyId?: number;
        withReviewId?: number;
        offset?: number;
      }
    >({
      query: ({
        url,
        artId,
        limit = 5,
        sortType = "popular",
        SID,
        withReplyId,
        withReviewId,
        offset,
      }) => {
        let purifiedUrl = url;
        if (url) {
          const [pathname, params] = url.split("?");
          if (params) {
            const urlParams = new URLSearchParams(params);

            if (limit) urlParams.delete("limit");
            if (offset) urlParams.delete("offset");
            if (sortType) urlParams.delete("o");
            if (withReplyId) urlParams.delete("with_reply_id");
            if (withReviewId) urlParams.delete("with_review_id");

            purifiedUrl = `${pathname}?${urlParams.toString()}`;
          }
        }

        return {
          url: purifiedUrl
            ? `/foundation/${purifiedUrl}`
            : `/foundation/api/arts/${artId}/reviews`,
          params: {
            limit,
            o: sortType,
            with_reply_id: withReplyId,
            with_review_id: withReviewId,
            offset,
          },
          headers: SID
            ? {
                "Session-Id": SID,
              }
            : undefined,
        };
      },
      providesTags: result => {
        return result
          ? [
              ...result.payload.data.map(({ id }: ArtReviewSerializer) => ({
                type: TagsType.ART as const,
                id,
              })),
              { type: TagsType.ART, id: "LIST" },
            ]
          : [{ type: TagsType.ART, id: "LIST" }];
      },
    }),

    getUserArtReviews: build.query<
      IApiResponse<UsersReviewsSerializer[]>,
      { artId: number; limit: number; SID?: string }
    >({
      query: ({ artId, limit = 10, SID }) => ({
        url: `/foundation/api/arts/${artId}/user-reviews`,
        params: { limit },
        headers: SID
          ? {
              "Session-Id": SID,
            }
          : undefined,
      }),
    }),

    addArtReview: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { artId: number; text: string }
    >({
      query: ({ artId, text }) => ({
        url: `/foundation/api/arts/${artId}/reviews`,
        method: "POST",
        body: { text },
      }),
    }),
    // TODO копипаста с карточки книги по правильному надо мержить ответы, а не хранить их в локальном стейте,
    // но для начала надо переписать всю систему комментариев
    getSeriesReviews: build.query<
      IApiResponse<ArtReviewSerializer[]>,
      {
        url?: string;
        seriesId: number;
        limit?: number;
        sortType?: "new" | "popular";
        withReplyId?: number;
        withReviewId?: number;
      }
    >({
      query: ({
        url,
        seriesId,
        limit,
        sortType = "popular",
        withReplyId,
        withReviewId,
      }) => {
        let purifiedUrl = url;
        if (url) {
          const [pathname, params] = url.split("?");
          if (params) {
            const urlParams = new URLSearchParams(params);

            if (limit) urlParams.delete("limit");
            if (sortType) urlParams.delete("o");
            if (withReplyId) urlParams.delete("with_reply_id");
            if (withReviewId) urlParams.delete("with_review_id");

            purifiedUrl = `${pathname}?${urlParams.toString()}`;
          }
        }

        return {
          url: purifiedUrl
            ? `/foundation/${purifiedUrl}`
            : `/foundation/api/series/${seriesId}/reviews`,
          params: {
            limit,
            o: sortType,
            with_reply_id: withReplyId,
            with_review_id: withReviewId,
          },
        };
      },
    }),

    getUserSeriesReviews: build.query<
      IApiResponse<UsersReviewsSerializer[]>,
      { seriesId: number; limit: number }
    >({
      query: ({ seriesId, limit = 10 }) => ({
        url: `/foundation/api/series/${seriesId}/user-reviews`,
        params: { limit },
      }),
    }),

    addSeriesReview: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { seriesId: number; text: string }
    >({
      query: ({ seriesId, text }) => ({
        url: `/foundation/api/series/${seriesId}/reviews`,
        method: "POST",
        body: { text },
      }),
    }),

    updateReview: build.mutation<IResponse, { reviewId: number; text: string }>(
      {
        query: ({ reviewId, text }) => ({
          url: `/foundation/api/reviews/${reviewId}`,
          method: "PUT",
          body: { text },
        }),
      }
    ),

    addReviewLike: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { reviewId: number }
    >({
      query: ({ reviewId }) => ({
        url: `/foundation/api/reviews/${reviewId}/likes`,
        method: "PUT",
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          reviewsApi.util.updateQueryData("getReview", params, draftReview => {
            const isArticleDislikedByUser =
              draftReview.is_disliked_by_current_user;
            Object.assign<
              SinglePageReviewSerializer,
              Partial<SinglePageReviewSerializer>
            >(draftReview, {
              is_liked_by_current_user: true,
              likes_count: draftReview.likes_count + 1,
              ...(isArticleDislikedByUser && {
                is_disliked_by_current_user: false,
                dislikes_count: draftReview.dislikes_count - 1,
              }),
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),

    deleteReviewLike: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { reviewId: number }
    >({
      query: ({ reviewId }) => ({
        url: `/foundation/api/reviews/${reviewId}/likes`,
        method: "DELETE",
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          reviewsApi.util.updateQueryData("getReview", params, draftReview => {
            Object.assign<
              SinglePageReviewSerializer,
              Partial<SinglePageReviewSerializer>
            >(draftReview, {
              is_liked_by_current_user: false,
              likes_count: draftReview.likes_count - 1,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),

    addReviewDislike: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { reviewId: number }
    >({
      query: ({ reviewId }) => ({
        url: `/foundation/api/reviews/${reviewId}/dislikes`,
        method: "PUT",
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          reviewsApi.util.updateQueryData("getReview", params, draftReview => {
            const isArticleLikedByUser = draftReview.is_liked_by_current_user;

            Object.assign<
              SinglePageReviewSerializer,
              Partial<SinglePageReviewSerializer>
            >(draftReview, {
              is_disliked_by_current_user: true,
              dislikes_count: draftReview.dislikes_count + 1,
              ...(isArticleLikedByUser && {
                is_liked_by_current_user: false,
                likes_count: draftReview.likes_count - 1,
              }),
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),

    deleteReviewDislike: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { reviewId: number }
    >({
      query: ({ reviewId }) => ({
        url: `/foundation/api/reviews/${reviewId}/dislikes`,
        method: "DELETE",
      }),
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedQuote = dispatch(
          reviewsApi.util.updateQueryData("getReview", params, draftReview => {
            Object.assign<
              SinglePageReviewSerializer,
              Partial<SinglePageReviewSerializer>
            >(draftReview, {
              is_disliked_by_current_user: false,
              dislikes_count: draftReview.dislikes_count - 1,
            });
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedQuote.undo();
        }
      },
    }),

    addReviewReply: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { reviewId: number; text: string }
    >({
      query: ({ reviewId, text }) => ({
        url: `/foundation/api/reviews/${reviewId}/replies`,
        method: "POST",
        body: { text },
      }),
    }),

    addReviewComplain: build.mutation<
      IResponse,
      { reviewId: number; text: string; reason: number }
    >({
      query: ({ reviewId, text, reason }) => ({
        url: `/foundation/api/reviews/${reviewId}/complain`,
        method: "PUT",
        body: { text, reason },
      }),
    }),

    // TODO копипаста с карточки книги по правильному надо мержить ответы, а не хранить их в локальном стейте,
    // но для начала надо переписать всю систему комментариев
    getAuthorReviews: build.query<
      IApiResponse<ArtReviewSerializer[]>,
      {
        authorIdOrSlug: number | string;
        limit?: number;
        offset?: number;
        url?: string;
        sortType?: "new" | "popular";
        withReplyId?: number;
        withReviewId?: number;
      }
    >({
      query: ({
        authorIdOrSlug,
        limit,
        offset,
        url,
        sortType = "popular",
        withReplyId,
        withReviewId,
      }) => {
        let purifiedUrl = url;
        if (url) {
          const [pathname, params] = url.split("?");
          if (params) {
            const urlParams = new URLSearchParams(params);

            if (limit) urlParams.delete("limit");
            if (offset) urlParams.delete("offset");
            if (sortType) urlParams.delete("o");
            if (withReplyId) urlParams.delete("with_reply_id");
            if (withReviewId) urlParams.delete("with_review_id");

            purifiedUrl = `${pathname}?${urlParams.toString()}`;
          }
        }

        return {
          url: purifiedUrl
            ? `/foundation/${purifiedUrl}`
            : `/foundation/api/persons/${authorIdOrSlug}/reviews`,
          params: {
            limit,
            offset,
            o: sortType,
            with_reply_id: withReplyId,
            with_review_id: withReviewId,
          },
        };
      },
    }),

    getUserAuthorReviews: build.query<
      IApiResponse<UsersReviewsSerializer[]>,
      { authorId: number; limit: number }
    >({
      query: ({ authorId, limit = 10 }) => ({
        url: `/foundation/api/persons/${authorId}/user-reviews`,
        params: { limit },
      }),
    }),

    addAuthorReview: build.mutation<
      IApiResponse<ArtReviewSerializer>,
      { authorId: number; text: string }
    >({
      query: ({ authorId, text }) => ({
        url: `/foundation/api/persons/${authorId}/reviews`,
        method: "POST",
        body: { text },
      }),
    }),
  }),
});
